<template>
  <div class="d-flex flex-row w-100 h-100">
    <div class="tags-section d-flex flex-column ps-4">
      <div class="d-flex flex-column">
        <div
          class="px-4 py-5 d-flex justify-space-between flex-wrap align-center"
          style="gap: 15px">
          <v-sheet class="d-flex align-center" style="gap: 8px">
            <v-icon size="32" color="primary">mdi-view-list</v-icon>
            <span class="registerTitle" v-if="currentRegisterType"
              >{{ currentRegisterType }} Register</span
            >
          </v-sheet>
          <!-- Every register type: TagsRegisterService applies linkStatus to all of them (AVA-792). -->
          <LinkStatusToggle :icon-size="16" />
          <v-sheet
            :class="{
              'flex-grow-1': is3dOpened,
              'order-3': $vuetify.breakpoint.xs || $vuetify.breakpoint.sm || $vuetify.breakpoint.md,
            }"
            :width="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm ? '100%' : 250">
            <v-text-field
              class="customSearchBox"
              outlined
              rounded
              dense
              hide-details
              v-model="searchText"
              :loading="isLoading"
              placeholder="Search tags..."
              append-icon="mdi-magnify"></v-text-field>
          </v-sheet>
          <!-- <v-sheet class="d-flex align-center">
            <v-btn color="primary" outlined rounded @click="showAllTagsIn3d" class="mx-2">
              <v-icon left>mdi-cube-outline</v-icon> Isolate All
            </v-btn>
          </v-sheet> -->
        </div>
        <v-divider style="width: 99%"></v-divider>
      </div>
      <keep-alive>
        <router-view
          class="flex-grow-1"
          :include="['app-register']"
          ref="registerView"
          :currentRegisterType="currentRegisterType"
          @updateViewerStatusColors="updateViewerStatusColors"
          @showIn3d="showTagsOn3d"
          @edit-completed="handleEditComplete($event)"
          @show-viewers-section="onShowViewersSection"></router-view>
      </keep-alive>
    </div>
    <v-sheet
      v-show="viewerOpened"
      class="viewers-section-wrapper"
      style="width: 30vw; min-width: 400px; flex-shrink: 0;">
      <ViewersSection
        ref="viewersSection"
        :viewerTab.sync="viewerTab"
        :usePhotoSphereViewer="false"
        @closeViewersSection="onCloseViewer"
      />
    </v-sheet>

    <!-- This page is not inside MainView, so the document cards need their own workspace host -->
    <DocumentWorkspaceHost />
  </div>
</template>

<script>
  import ViewersSection from '@Dashboard/components/ViewersSection.vue';
  import LinkStatusToggle from '@/components/tags/LinkStatusToggle.vue';
  import DocumentWorkspaceHost from '@/features/Documents/components/DocumentWorkspaceHost.vue';
  import { mapGetters } from 'vuex';
  import permissions from '@Share/config/permissions';
  // import httpService from '@Utils/httpService';
  import { $has } from '@/utils';
  import { log, warn, error } from '@/utils/debugLogger';
  import { isRequestCancelled } from '@Tags/utils/registerLoadPolicy';

  export default {
    name: 'main-tags',
    data() {
      return {
        loadType: false,
        selectedFilter: [0],
        permissions,
        is3dOpened: false,
        currentRequestController: null,
      };
    },
    computed: {
      ...mapGetters('TAGS_REGISTER', {
        isLoading: 'GET_LOADING',
        entries: 'GET_ENTRIES ',
      }),
      currentRegisterType() {
        return this.$route.params.registerId;
      },
      linkStatusFilter() {
        return this.$store.getters['TAGS_REGISTER/GET_LINK_STATUS_FILTER'];
      },
      // Toolbar search. Committed through SET_OPTIONS (never written into the store object
      // directly); TagsRegister.vue's options watcher debounces it as typing.
      searchText: {
        get() {
          return this.$store.getters['TAGS_REGISTER/GET_OPTIONS'].search || '';
        },
        set(val) {
          // A new search starts on page 1 (page 3 of a 13-row result would be empty).
          this.$store.commit('TAGS_REGISTER/SET_OPTIONS', { search: val, page: 1 });
        },
      },
      isolatedTags: {
        get() {
          return this.$store.getters['TAGS_REGISTER/GET_ISOLATED_TAGS'];
        },
        set(value) {
          this.$store.commit('TAGS_REGISTER/SET_ISOLATED_TAGS', value);
        },
      },
      viewerOpened: {
        get() {
          return this.$store.getters['TAGS_REGISTER/GET_VIEWER_OPENED'];
        },
        set(val) {
          this.$store.commit('TAGS_REGISTER/SET_VIEWER_OPENED', val);
        },
      },
      viewerTab: {
        get() {
          return this.$store.getters['TAGS_REGISTER/GET_VIEWER_TAB'];
        },
        set(val) {
          this.$store.commit('TAGS_REGISTER/SET_VIEWER_TAB', val);
        },
      },
    },
    // No load in mounted(): the immediate $route watcher already loads the register, and a
    // second call used to cancel that request and reload after 500 ms.
    beforeDestroy() {
      // Cancel any pending requests when component is destroyed
      this.cancelCurrentRequest();
    },
    methods: {
      $has,
      /**
       * Load page 1 after the result set changed (link-status toggle). If the page actually moves,
       * TagsRegister.vue's options watcher loads it (with the column filters); otherwise load here.
       */
      loadFromFirstPage() {
        if ((this.$store.getters['TAGS_REGISTER/GET_OPTIONS'].page || 1) !== 1) {
          this.$store.commit('TAGS_REGISTER/SET_OPTIONS', { page: 1 });
          return;
        }
        this.loadRegisterData();
      },
      /**
       * Load the register for a discrete event (open, register switch, link-status toggle, edit
       * saved). No debounce: LOAD_TAGS aborts any request still in flight, so the newest wins.
       */
      loadRegisterData() {
        this.loadType = true;
        const registerId = this.$route.params.registerId;
        const assetId = this.$route.params.id;

        // Load tags data for the specific register type
        this.$store.dispatch('TAGS_REGISTER/LOAD_TAGS', {
          tagType: registerId,
          assetId: assetId
        })
          .catch(err => {
            // A newer load aborted this one; nothing to report.
            if (isRequestCancelled(err)) {
              log('TagsRegister', 'Request was cancelled');
              return;
            }
            warn('Failed to load register data:', err);
          })
          .finally(() => {
            this.loadType = false;
          });
      },

      cancelCurrentRequest() {
        // Cancel any pending request in the store
        this.$store.dispatch('TAGS_REGISTER/CANCEL_CURRENT_REQUEST');
      },
      showTagsOn3d(tags) {
        if (!tags || (Array.isArray(tags) && tags.length === 0)) {
          return;
        }

        if (!(tags instanceof Array)) {
          tags = [tags];
        }

        this.isolatedTags = tags;
        this.viewerOpened = true;
        this.is3dOpened = true;
        this.viewerTab = '3D View'; // Force 3D tab when clicking 3D icon

        this.$nextTick(() => {
          this.loadViewerForCurrentTab();
        });
      },

      /**
       * Handle show-viewers-section event from child components
       * Opens the viewers section and switches to the requested tab
       */
      onShowViewersSection(tabName) {
        this.viewerOpened = true;
        this.viewerTab = tabName;
      },

      /**
       * Load the appropriate viewer based on the currently selected tab
       */
      loadViewerForCurrentTab() {
        // Don't require isolatedTags for Document tab - documents are loaded via activeDocument state
        if (!this.viewerOpened) {
          return;
        }

        // For Document tab, delegate to ViewersSection (which has its own watcher on activeDocument)
        if (this.viewerTab === 'Document') {
          this.$refs.viewersSection?.loadDocumentViewer?.();
          return;
        }

        // For other tabs, require isolatedTags
        if (!this.isolatedTags.length) {
          return;
        }

        switch (this.viewerTab) {
          case '3D View':
            this.isolateTagsIn3dViewer();
            break;
          case '2D View':
            this.isolateTagsIn2dViewer();
            break;
          case '360 View':
            this.isolateTagsIn360Viewer();
            break;
          default:
            warn('TagsIndex', 'loadViewerForCurrentTab: Unknown tab', this.viewerTab);
        }
      },

      /**
       * Isolate tags in the 3D viewer
       */
      async isolateTagsIn3dViewer() {
        /* AVA-492 explicit-viewer */
        const tags = this.isolatedTags;
        const dbIds = tags
          .flatMap((t) => t.children?.map((c) => c.dbId) || [])
          .filter(Boolean);

        await this.$refs.viewersSection?.load3dViewer?.();

        if (dbIds.length) {
          this.$store.state.forgeService?.getMainExtension()?.isolateDbIds(dbIds);
        } else {
          const point = tags[0]?.point || tags[0]?.position;
          if (point) {
            // tags[0].point/position are WORLD space; zoomToPoint3dViewer feeds
            // navigation.fitBounds (VIEWER space) and builds its box with a viewer-unit
            // offset (1/unitScale), so convert. Mirrors the getPointInView() callers.
            const viewerPoint =
              this.$store.state.forgeService?.worldToView3d?.(point, point) ?? point;
            this.$store.state.forgeService?.zoomToPoint3dViewer?.(viewerPoint);
          }
        }
      },

      /**
       * Isolate tags in the 2D viewer
       */
      async isolateTagsIn2dViewer() {
        try {
          const tags = this.isolatedTags;
          if (!tags.length) return;

          // Get 3D points from tags
          const points = tags
            .map((t) => t.point || t.position)
            .filter(Boolean);

          if (!points.length) return;

          // Find the level containing the first point
          const forgeService = this.$store.state.forgeService;
          const level = forgeService?.getLevelContainsPoint?.(points[0]) || this.$store.state.levels?.[0];

          if (!level?.planUrn) {
            this.$dialog?.notify?.warning?.('No floor plan available for this location');
            return;
          }

          this.$store.state.selectedLevel = level;

          // Load 2D viewer and zoom to point
          await this.$refs.viewersSection?.load2dViewer?.(level.planUrn);

          if (points.length > 0) {
            this.$store.dispatch('zoomToPointIn2D', {
              point3d: points[0],
              shouldZoom: true,
              callback: () => {
                forgeService?.getLabels2DExtension?.()?.createHighlightingTags?.(tags);
              },
            });
          }
        } catch (err) {
          error('Failed to show tags in 2D view:', err);
          this.$dialog?.notify?.error?.('Failed to show tags in 2D view');
        }
      },

      /**
       * Isolate tags in the 360 viewer
       */
      async isolateTagsIn360Viewer() {
        /* AVA-492 explicit-viewer */
        try {
          const tag = this.isolatedTags[0];
          if (!tag) return;

          // Find the nearest 360 image to this tag
          // Tags in the register are ForgeModel instances with getNearest360Image() method
          let imageToLoad = null;

          // Method 1: Check for linked 360 image first
          if (tag.linked360ImageInfo?.imageId) {
            await this.$store.dispatch('AI_INSPECT/GET_IMAGES');
            const images = this.$store.getters['AI_INSPECT/IMAGES'];
            imageToLoad = images?.find((img) => img.id == tag.linked360ImageInfo.imageId);
          }

          // Method 2: Fall back to getNearest360Image() if tag has this method
          if (!imageToLoad && typeof tag.getNearest360Image === 'function') {
            imageToLoad = tag.getNearest360Image();
          }

          // Method 3: Manual search using forgeService
          if (!imageToLoad) {
            const forgeService = this.$store.state.forgeService;
            const point = tag.point || tag.position;
            if (point && forgeService?.getClosestImageToPoint) {
              // tag.point/position are WORLD space; getClosestImageToPoint compares against
              // getCameraPositionInViewer() (VIEWER space). Convert here — mirrors the
              // correct path (tag.getNearest360Image → getPointInView). Methods 1/2 above
              // usually resolve the image; this manual fallback must convert too.
              const viewerPoint = forgeService.worldToView3d?.(point, point) ?? point;
              imageToLoad = forgeService.getClosestImageToPoint(viewerPoint);
            }
          }

          if (!imageToLoad) {
            this.$dialog?.notify?.warning?.('No 360 image found near this tag');
            return;
          }

          // Wait for ViewersSection to render
          await this.$nextTick();

          // Load the 360 viewer - ViewersSection handles 3D-to-yaw/pitch conversion
          await this.$refs.viewersSection?.loadPannellumViewer?.(
            imageToLoad,
            tag, // Pass tag for 3D point conversion
            'pannellum-viewer'
          );
        } catch (err) {
          error('Failed to load 360 viewer:', err);
          this.$dialog?.notify?.error?.('Failed to load 360 viewer');
        }
      },

      updateViewerStatusColors() {
        // Status colors are handled differently in ViewersSection
        // This method is kept for backward compatibility but may not be needed
      },

      onCloseViewer() {
        this.viewerOpened = false;
        this.is3dOpened = false;
        this.isolatedTags = [];
        this.$store.commit('SET_VIEWER_TYPE', '2d');
      },

      showAllTagsIn3d() {
        if (this.entries.some((t) => t.children?.length)) {
          this.showTagsOn3d(this.entries);
          return;
        }
      },
      handleEditComplete() {
        this.loadRegisterData();
      },
    },
    components: {
      ViewersSection,
      DocumentWorkspaceHost,
      LinkStatusToggle,
    },
    watch: {
      $route: {
        immediate: true,
        deep: true,
        handler(val, oldVal) {
          if (val.params.registerId) {
            if (
              oldVal &&
              oldVal.params.registerId &&
              oldVal.params.registerId == val.params.registerId
            ) {
              return;
            }
            // Another register: its columns differ, so the old column filters and page do not apply.
            // Reset both before the load (TagsRegister.vue is resetting and skips its own watcher).
            this.$store.commit('TAGS_REGISTER/SET_COLUMN_FILTERS', {});
            this.$store.commit('TAGS_REGISTER/SET_OPTIONS', { page: 1 });
            this.loadRegisterData();
          }
        },
      },
      // NOTE: options watcher removed - TagsRegister.vue handles pagination/search/sorting
      // to avoid duplicate requests and to include columnFilters in the request
      linkStatusFilter: {
        handler() {
          this.loadFromFirstPage();
        },
      },
      viewerTab: {
        handler() {
          if (this.viewerOpened) {
            this.loadViewerForCurrentTab();
          }
        },
      },
    },
  };
</script>

<style lang="scss">
  .registerTitle {
    font-size: 24px;
    font-weight: 500;
    line-height: 36px;
    letter-spacing: -0.01em;
    text-align: left;
  }

  .tags-section {
    min-width: 50%;
    flex: 1;
  }

  .customSearchBox {
    .v-input__control .v-input__slot {
      padding-right: 5px;
      .v-input__append-inner {
        margin-top: 5px !important;
        .v-input__icon--append {
          border-radius: 50%;
          background-color: var(--v-primary-base);
          width: 30px;
          height: 30px;
          i {
            color: var(--v-white-base) !important;
          }
        }
      }
    }
  }
</style>
